import React, { useContext, useEffect, useRef, useState } from "react";
import { withRouter } from "react-router-dom";
import jwtDecode from "jwt-decode";
import { Box, Button, Paper, Typography } from "@mui/material";

import logodark from "../../assets/images/Expanded_Colored_PoweredBy.png";
import spinner from "../../assets/images/aws/loadingGif.gif";
import { BackgroundPattern } from "../../components/BackgroundPattern";
import { OrgContext } from "../../OrgContext";
import { postSsoExchange, postSsoIdpInitExchange } from "../../helpers/auth_aws_helper";
import { getApiErrorMessage } from "../../helpers/aws_api_helper";
import { APP_ROLES, resolveUserRole } from "../../helpers/jwt-token-access/userRole";
import {
  SSO_EMAIL_KEY,
  SSO_STATE_KEY,
  SSO_VERIFIER_KEY,
  SSO_REDIRECT_URI_KEY,
  SSO_GENERIC_ERROR,
  clearSsoSessionKeys,
} from "../../helpers/ssoSession";

const SsoCallback = ({ history, location }) => {
  const { setHasSelectedOrganization } = useContext(OrgContext);
  const exchangeStartedRef = useRef(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    // The authorization code is single-use; never exchange twice for one callback.
    if (exchangeStartedRef.current) return;
    exchangeStartedRef.current = true;

    const params = new URLSearchParams(location.search);
    const code = params.get("code");
    const returnedState = params.get("state");
    const cognitoError = params.get("error");

    const email = sessionStorage.getItem(SSO_EMAIL_KEY);
    const storedState = sessionStorage.getItem(SSO_STATE_KEY);
    const codeVerifier = sessionStorage.getItem(SSO_VERIFIER_KEY);
    const redirectUri = sessionStorage.getItem(SSO_REDIRECT_URI_KEY);

    // Removing the keys and the query string up front means a page refresh cannot re-run the exchange.
    clearSsoSessionKeys();
    history.replace(location.pathname);

    if (cognitoError || !code) {
      setErrorMessage(SSO_GENERIC_ERROR);
      return;
    }

    // Okta tile sign-ins arrive without `state`. A present but unmatched `state` must never
    // fall back to the tile exchange, or a stale or forged callback would be accepted.
    const isTileSignIn = !returnedState;
    if (
      !isTileSignIn &&
      (!storedState || returnedState !== storedState || !email || !codeVerifier || !redirectUri)
    ) {
      setErrorMessage(SSO_GENERIC_ERROR);
      return;
    }

    (async () => {
      try {
        const response = isTileSignIn
          ? await postSsoIdpInitExchange({
              code,
              redirectUri: `${window.location.origin}/auth/callback`,
            })
          : await postSsoExchange({
              email,
              code,
              state: returnedState,
              codeVerifier,
              redirectUri,
            });

        if (response?.status !== 200 || !response?.data?.idToken) {
          setErrorMessage(getApiErrorMessage(response) || SSO_GENERIC_ERROR);
          return;
        }

        const decoded = jwtDecode(response.data.idToken);
        // Without an app role, every protected route redirects to /login, which wipes the new session.
        if (!APP_ROLES.includes(resolveUserRole(decoded["cognito:groups"]))) {
          setErrorMessage(SSO_GENERIC_ERROR);
          return;
        }

        const authUser = { ...response.data, token: response.data.accessToken };
        localStorage.setItem("authUser", JSON.stringify(authUser));
        localStorage.setItem("activeExp", JSON.stringify(decoded.exp));
        localStorage.setItem("hasSelectedOrganization", false);
        localStorage.removeItem("lastActiveRoute");
        setHasSelectedOrganization(false);
        history.replace("/orgSelection");
      } catch (error) {
        setErrorMessage(getApiErrorMessage(error?.response || error) || SSO_GENERIC_ERROR);
      }
    })();
  }, [history, location, setHasSelectedOrganization]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Paper
        elevation={3}
        sx={{ position: "relative", zIndex: 1, width: "100%", maxWidth: 440, p: 4, textAlign: "center" }}
      >
        <Box sx={{ mb: 2 }}>
          <img src={logodark} height="60" alt="logo" />
        </Box>
        {errorMessage ? (
          <>
            <Typography component="h1" variant="h6" gutterBottom>
              Sign-in failed
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {errorMessage}
            </Typography>
            <Button variant="contained" onClick={() => history.replace("/login")}>
              Back to sign in
            </Button>
          </>
        ) : (
          <>
            <img src={spinner} height={40} alt="Loading" />
            <Typography variant="body1" sx={{ mt: 2 }}>
              Signing you in...
            </Typography>
          </>
        )}
      </Paper>
      <BackgroundPattern />
    </Box>
  );
};

export default withRouter(SsoCallback);
